import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

export default function Register() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, valid, invalid, submitting, done
  const [error, setError] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await fetch(`/api/invitations/validate?token=${token}`);
      const data = await res.json();
      if (data.valid) {
        setStatus('valid');
      } else {
        setInviteError(data.error || 'Enlace no válido');
        setStatus('invalid');
      }
    } catch {
      setInviteError('Error de conexión');
      setStatus('invalid');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch('/api/invitations/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          name: form.name,
          email: form.email,
          password: form.password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Error al registrarse');
        setStatus('valid');
        return;
      }

      setStatus('done');
    } catch {
      setError('Error de conexión');
      setStatus('valid');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0f1117' }}>
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-lg" style={{ backgroundColor: '#FFB800', color: '#0f1117' }}>E</div>
            <h1 className="text-2xl font-bold text-white tracking-wide">ENCOM TOOLS</h1>
          </div>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {status === 'done' ? 'Registro completado' : 'Crear tu cuenta'}
          </p>
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-white/20 rounded-full mx-auto mb-3" style={{ borderTopColor: '#FFB800', animation: 'spin 0.8s linear infinite' }} />
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Verificando invitación...</p>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {/* Invalid token */}
        {status === 'invalid' && (
          <div className="bg-white rounded-2xl p-6 shadow-2xl text-center">
            <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#fef2f2' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 className="font-bold text-lg text-gray-900 mb-2">Enlace no válido</h2>
            <p className="text-sm text-gray-500 mb-4">{inviteError}</p>
            <p className="text-xs text-gray-400">Pide a un administrador que te envíe un nuevo enlace de invitación.</p>
          </div>
        )}

        {/* Registration form */}
        {(status === 'valid' || status === 'submitting') && (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-2xl">
            <p className="text-sm text-gray-500 mb-4">Has sido invitado a Encom Tools. Completa tus datos para crear tu cuenta.</p>

            {error && (
              <div className="text-sm px-3 py-2 rounded-lg mb-4" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>{error}</div>
            )}

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Tu nombre"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#FFB800' }}
                required
                autoFocus
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="tu@encom.es"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#FFB800' }}
                required
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
              <input
                type="password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#FFB800' }}
                required
                minLength={8}
              />
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                placeholder="Repite la contraseña"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#FFB800' }}
                required
                minLength={8}
              />
            </div>

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="btn-accent w-full py-2.5 rounded-lg text-sm transition-colors"
            >
              {status === 'submitting' ? 'Registrando...' : 'Crear cuenta'}
            </button>
          </form>
        )}

        {/* Success */}
        {status === 'done' && (
          <div className="bg-white rounded-2xl p-6 shadow-2xl text-center">
            <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#e8f5e9' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 className="font-bold text-lg text-gray-900 mb-2">Cuenta creada</h2>
            <p className="text-sm text-gray-500 mb-5">Un administrador revisará tu solicitud. Recibirás acceso cuando sea aprobada.</p>
            <button
              onClick={() => navigate('/login')}
              className="btn-dark w-full py-2.5 rounded-lg text-sm"
            >
              Ir al login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
